import React from 'react';
import PropTypes from 'prop-types';

import { Spinner } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';

import {
  BG_1,
  BORDER_2,
  FG_1,
  FG_2,
  FG_3,
  PRIMARY_COLOR,
  RADIUS_LG,
  SHADOW_SM,
  SHADOW_MD,
} from 'components/upb_viewer_beta/css/betaTokens';
import { bandTier } from 'components/upb_match_explorer/constants';
import { fmtPct } from 'components/upb_match_explorer/format';

function MatchRangeTiles({ summary, loading, onBandClick }) {
  if (loading) {
    return (
      <div className={css(styles.loading)}>
        <Spinner animation="border" role="status" size="sm" />
      </div>
    );
  }

  const bands = summary?.bands || [];
  const maxCount = Math.max(1, ...bands.map(b => b.count));

  return (
    <div className={css(styles.grid)}>
      {bands.map(band => {
        const tier = bandTier(band.band);
        return (
          <button
            key={band.band}
            type="button"
            className={css(styles.tile)}
            onClick={() => onBandClick(band.band)}
            aria-label={`${band.label} UPB match, ${band.count} partners. Open.`}
          >
            <span className={css(styles.stripe)} style={{ backgroundColor: tier.color }} />
            <span
              className={css(styles.range)}
              style={{ backgroundColor: tier.bg, color: tier.text }}
            >
              <span className={css(styles.dot)} style={{ backgroundColor: tier.color }} />
              {band.label}
            </span>
            <span className={css(styles.count)}>
              {band.count}
              <span className={css(styles.countUnit)}>
                {band.count === 1 ? 'partner' : 'partners'}
              </span>
            </span>
            <span className={css(styles.caption)}>{band.name} · UPB match rate</span>
            <span className={css(styles.shareTrack)}>
              <span
                className={css(styles.shareFill)}
                style={{ width: `${(band.count / maxCount) * 100}%`, backgroundColor: tier.color }}
              />
            </span>
            <span className={css(styles.foot)}>
              <span className={css(styles.share)}>{fmtPct(band.share)} of all partners</span>
              <span className={css(styles.explore)}>Explore →</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

const styles = StyleSheet.create({
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    color: PRIMARY_COLOR,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 16,
    '@media (max-width: 900px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
    '@media (max-width: 560px)': { gridTemplateColumns: '1fr' },
  },
  tile: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
    background: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    padding: '22px 22px 20px',
    boxShadow: SHADOW_SM,
    cursor: 'pointer',
    overflow: 'hidden',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease',
    ':hover': { transform: 'translateY(-3px)', boxShadow: SHADOW_MD },
  },
  stripe: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
  },
  range: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    alignSelf: 'flex-start',
    fontSize: 12.5,
    fontWeight: 600,
    padding: '4px 9px',
    borderRadius: 20,
    marginBottom: 14,
    marginTop: 6,
  },
  dot: { width: 8, height: 8, borderRadius: '50%' },
  count: {
    fontSize: 42,
    fontWeight: 700,
    lineHeight: 1,
    color: FG_1,
    fontVariantNumeric: 'tabular-nums',
  },
  countUnit: { fontSize: 15, fontWeight: 600, color: FG_3, marginLeft: 7 },
  caption: { fontSize: 13, color: FG_2, marginTop: 8 },
  shareTrack: {
    marginTop: 14,
    height: 6,
    borderRadius: 6,
    backgroundColor: BORDER_2,
    overflow: 'hidden',
  },
  shareFill: { display: 'block', height: '100%', borderRadius: 6 },
  foot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 14,
    paddingTop: 12,
    borderTop: `1px solid ${BORDER_2}`,
  },
  share: { fontSize: 12, color: FG_3, fontVariantNumeric: 'tabular-nums' },
  explore: { fontSize: 12.5, fontWeight: 600, color: PRIMARY_COLOR },
});

MatchRangeTiles.propTypes = {
  summary: PropTypes.shape({
    total: PropTypes.number,
    bands: PropTypes.arrayOf(
      PropTypes.shape({
        band: PropTypes.number,
        label: PropTypes.string,
        name: PropTypes.string,
        count: PropTypes.number,
        share: PropTypes.number,
      }),
    ),
  }),
  loading: PropTypes.bool,
  onBandClick: PropTypes.func.isRequired,
};

export default MatchRangeTiles;
